import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import {
  ClienteFincaLoteItem,
  ClienteFincaLoteService,
} from "../../../generated/grpc/terceros_lote_pb";
import { grpcLoteDatabase } from "../database/GrpcLoteDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { CultivoResponse } from "../../catalogos/aplicaciones/response/CultivoResponse";
import { ClienteFincaLoteResponse } from "../response/ClienteFincaLoteResponse";

/** Variante gRPC-Web (con delta real, por finca) de `LoteGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `ClienteFincaLoteResponse[]` -mismo shape que ya
 *  usa el resto de la app (~48 archivos leen este tipo, no solo los que llaman
 *  a este servicio)-, así que el swap queda contenido acá: nada más necesita
 *  enterarse de que el transporte cambió. La conversión rellena con valores
 *  inertes los campos que se comprobó que nadie lee (`clienteId`,
 *  `monitoreoCantidadArboles`, `loteOrigenId`, `tipoMonitoreo`,
 *  `tipoMonitoreoId` del cultivo — ver el comentario de `ClienteFincaLoteItem`
 *  en el proto). */
class LoteGrpcService {
  constructor(private readonly _fincaId: string) {}

  /** Sincroniza (si el cooldown lo permite) y devuelve el listado completo
   *  de la finca desde IndexedDB, ya en el shape REST. */
  async getListAsync(): Promise<ClienteFincaLoteResponse[]> {
    await this._syncAsync();
    const items = await grpcLoteDatabase.getListByFincaIdAsync<ClienteFincaLoteItem>(
      this._fincaId,
    );
    return items.map(toClienteFincaLoteResponse);
  }

  /** Invalida el cooldown para forzar que el próximo `getListAsync()` vuelva
   *  a pedir delta al server. */
  clearCache(): void {
    serviceSync.reset(`grpc:lotes:${this._fincaId}`);
  }

  /** Fuerza que el próximo `getListAsync()` traiga todos los lotes de la
   *  finca, no solo el delta: borra el watermark y el cooldown. Lo llama todo
   *  guardado que cambie datos de un lote (movimientos de ganadería, editar
   *  potrero, árboles a monitorear, wizard), para no depender de que el
   *  delta traiga el cambio. */
  async recargarTodoAsync(): Promise<void> {
    await grpcLoteDatabase.borrarSincronizacionAsync(this._fincaId);
    this.clearCache();
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:lotes:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcLoteDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(ClienteFincaLoteService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcLoteDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createLoteGetListByFincaIdService`). */
export const fx_createLoteGrpcService = createCachedFactory(
  (fincaId: string) => new LoteGrpcService(fincaId),
);

/** Adapta un `ClienteFincaLoteItem` (gRPC) al shape `ClienteFincaLoteResponse`
 *  (REST) — ver el comentario de la clase. Exportada porque `LoteSaveGrpcService`
 *  también la necesita para convertir la respuesta de `Guardar`. */
export function toClienteFincaLoteResponse(
  lote: ClienteFincaLoteItem,
): ClienteFincaLoteResponse {
  const cultivo: CultivoResponse | null = lote.cultivo
    ? {
        id: lote.cultivo.id,
        nombre: lote.cultivo.nombre,
        tipoMonitoreoId: 0,
        isMonitoreoCafe: lote.cultivo.isMonitoreoCafe,
        cantidadPlagas: lote.cultivo.cantidadPlagas,
      }
    : null;

  return {
    id: lote.id,
    isEliminado: lote.eliminado,
    clienteId: "",
    fincaId: lote.fincaId,
    nombreLote: lote.nombreLote,
    areaHectareas: lote.areaHectareas,
    areaCuadras: lote.areaCuadras,
    isAreaProductiva: lote.isAreaProductiva,
    areaGeograficaPolygon: JSON.parse(lote.areaGeograficaPolygon),
    loteOrigenId: null,
    cultivo,
    ganaderiaCantidadAnimales: lote.ganaderiaCantidadAnimales,
    ganaderiaUltimoIngreso: epochMsToIso(lote.ganaderiaUltimoIngreso),
    ganaderiaUltimaSalida: epochMsToIso(lote.ganaderiaUltimaSalida),
    ganaderiaPesoRealPromedioKg: lote.ganaderiaPesoRealPromedioKg ?? null,
    ganaderiaFechaUltimoPesaje: epochMsToIso(lote.ganaderiaFechaUltimoPesaje),
    ganaderiaPesoEstimadoTotalKg: lote.ganaderiaPesoEstimadoTotalKg ?? null,
    ganaderiaFechaUltimoPesoEstimado: epochMsToIso(lote.ganaderiaFechaUltimoPesoEstimado),
    ganaderiaGananciaDiariaTotalKg: lote.ganaderiaGananciaDiariaTotalKg ?? null,
    monitoreoCantidadArboles: 0,
  };
}

function epochMsToIso(ms: bigint | undefined): string | null {
  return ms == null ? null : new Date(Number(ms)).toISOString();
}
